import * as actions from '@returnmates/client-core/src/constants/actionTypes'
import { Package } from '@returnmates/client-core/src/graphql/generated/api'
import { getCurrentPackage as getCurrentPackageSelector } from '@returnmates/client-core/src/selectors/packages'
import { SnackBarStatuses } from '@returnmates/client-core/src/type'
import errorMapper from '@returnmates/client-core/src/utils/errorMapper'
import { createAsyncAction } from '@returnmates/client-core/src/utils/reduxUtils'
import Button from '@returnmates/ui-core/src/components/Button'
import Modal from '@returnmates/ui-core/src/components/Modal'
import { Dispatch, memo, SetStateAction, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import useStyles from '../styles'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  addressId: string
  setIsPackageUpdateLoading: Dispatch<SetStateAction<boolean>>
}

const MultiPackageModal = ({
  isOpen,
  onClose,
  onSubmit,
  addressId,
  setIsPackageUpdateLoading,
}: Props) => {
  const dispatch = useDispatch()
  const currentPackage: Package | null = useSelector(getCurrentPackageSelector)
  const classes = useStyles({})

  const updateTrip = useCallback(
    async (addressId: string) => {
      try {
        setIsPackageUpdateLoading(true)

        await createAsyncAction(
          dispatch,
          actions.updateTripPackagesAddressId.request({
            id: currentPackage?.tripId,
            input: {
              addressId,
            },
          }),
        )
      } catch (error) {
        const { message } = error as Error

        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: errorMapper(message || (error as string)),
          }),
        )
      } finally {
        setIsPackageUpdateLoading(false)
      }
    },
    [setIsPackageUpdateLoading, dispatch, currentPackage?.tripId],
  )

  const createNewTrip = useCallback(
    async (addressId: string) => {
      try {
        setIsPackageUpdateLoading(true)

        await createAsyncAction(
          dispatch,
          actions.splitPackageByAddressId.request({
            id: currentPackage?.id,
            input: {
              addressId,
            },
          }),
        )
      } catch (error) {
        dispatch(
          actions.addSnackBar.request({
            type: SnackBarStatuses.ERROR,
            message: error,
          }),
        )
      } finally {
        setIsPackageUpdateLoading(false)
      }
    },
    [setIsPackageUpdateLoading, dispatch, currentPackage?.id],
  )

  const onUpdateTrip = useCallback(async () => {
    await updateTrip(addressId)
    await onSubmit()

    onClose()
  }, [updateTrip, addressId, onSubmit, onClose])

  const onCreateNewTrip = useCallback(async () => {
    await createNewTrip(addressId)
    await onSubmit()

    onClose()
  }, [createNewTrip, addressId, onSubmit, onClose])

  return (
    <Modal isOpen={isOpen} onClose={onClose} width={448}>
      <div className={classes.modalWrapper}>
        <p className={classes.missingLabelTitile}>Warning</p>
        <p className={classes.missingLabelText}>
          This package belongs to a multi-package trip. Do you want to update the address for all
          packages?
        </p>
        <div className={classes.multiPackageModalButtons}>
          <Button
            className={classes.multiPackageModalButton}
            onClick={onUpdateTrip}
            label="Update trip"
          />
          <Button
            className={classes.multiPackageModalButton}
            onClick={onCreateNewTrip}
            label="Create New Trip"
          />
          <Button
            className={classes.multiPackageModalButton}
            onClick={onClose}
            label="Cancel"
            cancelButton
          />
        </div>
      </div>
    </Modal>
  )
}

export default memo(MultiPackageModal)
